import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import styled from 'styled-components';
import { FaUser, FaUserPlus, FaInstagram, FaChartBar, FaCog, FaBars } from 'react-icons/fa';
import logo from '../../logo.png';

const NavbarContainer = styled.nav`
  position: relative;
  /* Stack above <main> so the hamburger menu is not covered by page controls (e.g. sportsbook z-index on model/Team Picks). */
  z-index: 2000;
  background-color: ${props => props.theme.navbarBg};
  color: ${props => props.theme.inverseText};
  padding: 0.5rem 1rem;
  transition: background-color 0.3s ease;
`;

const NavbarContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
`;

const CenterContent = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  height: 48px;
`;

const LogoText = styled.span`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${props => props.theme.inverseText};
  margin-left: 10px;
  letter-spacing: 0.5px;
  transition: color 0.2s;

  &:hover {
    color: ${props => props.theme.primary};
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;
`;

const NavLink = styled(Link)`
  color: ${props => props.theme.inverseText};
  text-decoration: none;
  padding: 0.5rem 0;
  
  &:hover {
    color: ${props => props.theme.primary};
  }
`;

const AuthButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &.primary {
    background-color: ${props => props.theme.primary};
    color: ${props => props.theme.inverseText};
    border: none;
    
    &:hover {
      background-color: ${props => props.theme.primaryHover};
    }
  }
  
  &.secondary {
    background-color: transparent;
    color: ${props => props.theme.inverseText};
    border: 1px solid ${props => props.theme.primary};
    
    &:hover {
      background-color: rgba(66, 153, 225, 0.1);
    }
  }
`;



const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled.button`
  color: ${props => props.theme.inverseText};
  background: none;
  border: none;
  padding: 0.5rem 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: inherit;
  
  &:hover {
    color: ${props => props.theme.primary};
  }
`;

const DropdownContent = styled.div`
  display: ${props => props.isOpen ? 'block' : 'none'};
  position: absolute;
  background-color: ${props => props.theme.dropdownBg};
  min-width: 160px;
  max-height: calc(100vh - 4rem);
  overflow-y: auto;
  box-shadow: ${props => props.theme.shadowHover};
  z-index: 1000;
  border-radius: 0.25rem;
  top: 100%;
  left: 0;
  padding: 0.25rem 0;
  margin-top: -0.5rem;
  transition: all 0.2s ease;
`;

const DropdownLink = styled.button`
  color: ${props => props.theme.inverseText};
  padding: 6px 12px 6px 16px;
  text-decoration: none;
  display: block;
  font-size: 0.9rem;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;

  &:hover,
  &:focus {
    background-color: ${props => props.theme.tertiaryBg};
    color: ${props => props.theme.primary};
  }
`;

/* Tip button temporarily removed until config is fixed
const TipJarButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 0.25rem;
  transition: background 0.2s;
  height: 40px;
  width: 40px;
  color: ${props => props.theme.primary};
  text-decoration: none;

  &:hover {
    background: rgba(66, 153, 225, 0.1);
    color: ${props => props.theme.primary};
    text-decoration: none;
  }
`;
*/

const InstagramButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 0.25rem;
  transition: background 0.2s;
  height: 40px;
  width: 40px;
  color: ${props => props.theme.primary};

  &:hover {
    background: rgba(66, 153, 225, 0.1);
  }
`;

const MetricsButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0.5rem;
  margin-right: 0.5rem;
  cursor: pointer;
  border-radius: 0.25rem;
  transition: background 0.2s;
  height: 40px;
  width: 40px;
  text-decoration: none;

  &:hover {
    background: rgba(66, 153, 225, 0.1);
  }
`;

const HamburgerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 0.25rem;
  transition: background 0.2s;
  height: 40px;
  width: 40px;
  color: ${props => props.theme.inverseText};

  &:hover {
    background: rgba(66, 153, 225, 0.1);
  }
`;

const DropdownSection = styled.div`
  padding: 2px 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${props => props.theme.border};
  }
`;

const DropdownSectionTitle = styled.div`
  color: ${props => props.theme.primary};
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 4px 12px 2px 12px;
  margin: 0 6px 2px 6px;
  background-color: ${props => props.theme.tertiaryBg};
  border-radius: 4px;
  text-align: left;
`;

/** Separates logout from main menu sections */
const DropdownFooter = styled.div`
  border-top: 1px solid ${(props) => props.theme.border};
  margin: 0.35rem 0 0;
  padding: 0.35rem 0 0.15rem;
`;

/** Logout at menu bottom: visually distinct from navigation links */
const DropdownLogoutLink = styled(DropdownLink)`
  font-weight: 600;
  color: ${(props) => props.theme.error};

  &:hover,
  &:focus {
    background-color: rgba(245, 101, 101, 0.14);
    color: ${(props) => props.theme.error};
  }
`;

const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);
  
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  const handleMenuToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  };
  
  const handleMenuItemClick = () => {
    setIsMenuOpen(false);
  };
  
  return (
    <NavbarContainer theme={theme}>
      <NavbarContent>
        <Logo to="/">
          <img src={logo} alt="SlickPicks Logo" style={{ height: '36px', display: 'block' }} />
          <LogoText theme={theme}>$lickPicks</LogoText>
        </Logo>
        <AuthButtons>
          {/* Tip button temporarily removed until config is fixed
          <TipJarButton
            to="/tip-jar"
            title="Tip Jar"
            theme={theme}
          >
            <FaGift size={24} style={{ color: theme.primary }} />
          </TipJarButton>
          */}
          <InstagramButton
            href="https://www.instagram.com/slickpicksbet/"
            target="_blank"
            rel="noopener noreferrer"
            title="Instagram"
            theme={theme}
          >
            <FaInstagram size={28} style={{ color: theme.primary }} />
          </InstagramButton>
          <DropdownContainer
            ref={menuRef}
            onMouseEnter={() => setIsMenuOpen(true)}
            onMouseLeave={() => setIsMenuOpen(false)}
          >
            <HamburgerButton
              theme={theme}
              onClick={handleMenuToggle}
              onTouchStart={(e) => e.stopPropagation()}
              title="Menu"
            >
              <FaBars size={20} />
            </HamburgerButton>
            <DropdownContent theme={theme} isOpen={isMenuOpen} style={{ right: 0, left: 'auto', minWidth: '200px' }}>
              {/* Account Section */}
              <DropdownSection theme={theme}>
                <DropdownSectionTitle theme={theme}>Account</DropdownSectionTitle>
                                 {isAuthenticated ? (
                   <>
                     <DropdownLink theme={theme} as={Link} to="/profile" onClick={handleMenuItemClick}>
                       Profile
                     </DropdownLink>
                     <DropdownLink theme={theme} as={Link} to="/dashboard" onClick={handleMenuItemClick}>
                       Dashboard
                     </DropdownLink>
                     <DropdownLink theme={theme} as={Link} to="/tracked-picks" onClick={handleMenuItemClick}>
                       Tracked Picks
                     </DropdownLink>
                   </>
                 ) : (
                   <>
                    <DropdownLink
                      theme={theme}
                      as={Link}
                      to="/login"
                      state={{ from: `${location.pathname}${location.search || ''}` }}
                      onClick={handleMenuItemClick}
                    >
                       Login
                     </DropdownLink>
                     <DropdownLink theme={theme} as={Link} to="/register" onClick={handleMenuItemClick}>
                       Sign Up
                     </DropdownLink>
                   </>
                 )}
              </DropdownSection>

                             {/* Picks Section */}
               <DropdownSection theme={theme}>
                 <DropdownSectionTitle theme={theme}>Picks</DropdownSectionTitle>
                 <DropdownLink 
                   theme={theme}
                   as={Link}
                   to="/game-picks"
                   onClick={handleMenuItemClick}
                 >
                   Game Picks
                 </DropdownLink>
                 <DropdownLink 
                   theme={theme}
                   as={Link}
                   to="/team-picks"
                   onClick={handleMenuItemClick}
                 >
                   Team Picks
                 </DropdownLink>
                 <DropdownLink 
                   theme={theme}
                   as={Link}
                   to="/mlb/total-bases"
                   onClick={handleMenuItemClick}
                 >
                   MLB Total Bases
                 </DropdownLink>
                <DropdownLink 
                  theme={theme}
                  as={Link}
                  to="/mlb/hit"
                  onClick={handleMenuItemClick}
                >
                  MLB Hit
                </DropdownLink>
                <DropdownLink 
                  theme={theme}
                  as={Link}
                  to="/mlb/two-hits"
                  onClick={handleMenuItemClick}
                >
                  MLB 2+ Hits
                </DropdownLink>
                <DropdownLink 
                  theme={theme}
                  as={Link}
                  to="/mlb/hr"
                  onClick={handleMenuItemClick}
                >
                  MLB Home Runs
                </DropdownLink>
              </DropdownSection>

               {/* Resources Section */}
               <DropdownSection theme={theme}>
                 <DropdownSectionTitle theme={theme}>Resources</DropdownSectionTitle>
                 <DropdownLink 
                   theme={theme}
                   as={Link}
                   to="/how-it-works"
                   onClick={handleMenuItemClick}
                 >
                   How It Works
                 </DropdownLink>
                 <DropdownLink 
                   theme={theme}
                   as={Link}
                   to="/model-stats"
                   onClick={handleMenuItemClick}
                 >
                   Model Stats
                 </DropdownLink>
                 <DropdownLink 
                   theme={theme}
                   as={Link}
                   to="/resources/odds-calculator"
                   onClick={handleMenuItemClick}
                 >
                   Odds Calculator
                 </DropdownLink>
                 <DropdownLink 
                   theme={theme}
                   as={Link}
                   to="/resources/parlay-calculator"
                   onClick={handleMenuItemClick}
                 >
                   Parlay Calculator
                 </DropdownLink>
                 <DropdownLink 
                   theme={theme}
                   as={Link}
                   to="/resources/betting-strategies"
                   onClick={handleMenuItemClick}
                 >
                   Betting Strategies
                 </DropdownLink>
              </DropdownSection>

              {/* Admin Section */}
              {isAuthenticated && user?.is_admin && (
                <DropdownSection theme={theme}>
                  <DropdownSectionTitle theme={theme}>
                    <FaCog size={12} style={{ marginRight: '6px', color: '#fbbf24', verticalAlign: 'middle' }} />
                    Admin
                  </DropdownSectionTitle>
                  <DropdownLink 
                    theme={theme}
                    as={Link} 
                    to="/admin/users" 
                    onClick={handleMenuItemClick}
                  >
                    User Management
                  </DropdownLink>
                  <DropdownLink 
                    theme={theme}
                    as={Link} 
                    to="/admin/run-job" 
                    onClick={handleMenuItemClick}
                  >
                    Run pipeline job
                  </DropdownLink>
                  <DropdownLink 
                    theme={theme}
                    as={Link} 
                    to="/admin/social-media" 
                    onClick={handleMenuItemClick}
                  >
                    Social Media Mgmt
                  </DropdownLink>
                  <DropdownLink 
                    theme={theme}
                    as={Link} 
                    to="/admin/email-preview" 
                    onClick={handleMenuItemClick}
                  >
                    Email preview
                  </DropdownLink>
                  {process.env.REACT_APP_DEBUG_TOOLS === 'true' && (
                    <DropdownLink 
                      theme={theme}
                      as={Link} 
                      to="/admin/debug-model" 
                      onClick={handleMenuItemClick}
                    >
                      Model Debug
                    </DropdownLink>
                  )}
                </DropdownSection>
              )}

              {isAuthenticated && (
                <DropdownFooter theme={theme}>
                  <DropdownLogoutLink
                    theme={theme}
                    onClick={() => {
                      handleLogout();
                      handleMenuItemClick();
                    }}
                  >
                    Logout
                  </DropdownLogoutLink>
                </DropdownFooter>
              )}
            </DropdownContent>
          </DropdownContainer>
        </AuthButtons>
      </NavbarContent>
    </NavbarContainer>
  );
};

export default Navbar; 